import React, { FormEvent, useEffect } from 'react'
import { useFetcher, useLoaderData, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { setLocale } from 'yup';

// components
import TextAreaInput from '../components/TextAreaInput/TextAreaInput';


// helper
import { getReservation, updateReservationStep3 } from '../helpers/localStorage';
import { IreservationStep3, TAllergies } from '../helpers/interfaces';
import CheckBoxInput from '../components/CheckBoxInput/CheckBoxInput';
import Allergies from '../components/Allergies/Allergies';
import RadioBoxInput from '../components/RadioBoxInput/RadioBoxInput';

setLocale({
  mixed: {
    required: 'Valore richiesto',
    default: 'Valore non valido',
  },
  string: {
    email: 'Deve essere un indirizzo email valido',
    min: 'Deve essere di almeno ${min} caratteri',
    max: 'Deve essere di massimo ${max} caratteri',
  },
  number: {
    min: 'Deve essere maggiore di ${min}',
    max: 'Deve essere minore di ${min}',
  },
});

export function step3Loader() {
  return getReservation();
}

export async function step3Action({ request }: { request: Request }) {
  const data = await request.formData();
  const { _action, ...values } = Object.fromEntries(data);

  if (_action === "step3") {
    try {

      updateReservationStep3({
        note: values.note as string,
        gdpr: (values.gdpr as string) === 'true' ? true : false,
        mailingList: (values.mailingList as string) === 'true' ? true : false,
        allergies: !!values.allergies ? (values.allergies as string).split(',') as TAllergies[] : [],
        menu: values.menu as string,
      });

      // return toast.success('Dati salvati correttamente', {
      //   autoClose: 1000,
      //   pauseOnFocusLoss: false,
      //   theme: "colored",
      //   position: "top-center",
      //   onClose: () => window.location.href = "/step4",
      // });

      // const baseUrl = process.env.REACT_APP_BASEURL;
      // return window.location.href = baseUrl + "/step4";

      return true;

    } catch (err) {
      console.error(err);
      throw new Error("C'è stato un problema con la tua prenotazione. Riprova più tardi.");
    }
  }
}

interface Props { }

const Step3page: React.FC<Props> = () => {

  const savedReservation = useLoaderData() as any;
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const [validateAll, setValidateAll] = React.useState(false);
  const [form, setForm] = React.useState<IreservationStep3>({
    note: savedReservation.note || '',
    gdpr: savedReservation.gdpr || false,
    mailingList: savedReservation.mailingList || false,
    menu: savedReservation.menu || '',
    allergies: savedReservation.allergies || [],
  });

  useEffect(() => {
    if (fetcher.data && fetcher.data === true) {
      setValidateAll(false);
      navigate('/step4');
    }
  }, [fetcher.data, navigate]);

  const step3Schema = yup.object({
    gdpr: yup.boolean().required().oneOf([true], 'Questo campo è richiesto'),
    note: yup.string().max(255).nullable(),
    mailingList: yup.boolean().required(),
    menu: yup.string().max(20).nullable(),
    allergies: yup.array().nullable(),
  });

  const isSubmitting = fetcher.state === "submitting";

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {

    e.preventDefault();
    setValidateAll(true);

    let formData = {
      gdpr: step3Schema.fields.gdpr.cast(form.gdpr),
      mailingList: step3Schema.fields.mailingList.cast(form.mailingList),
      menu: step3Schema.fields.menu.cast(form.menu),
      allergies: step3Schema.fields.allergies.cast(form.allergies),
      note: step3Schema.fields.note.cast(form.note),
    } as IreservationStep3;

    // Validate all fields together
    step3Schema.validate(formData, { strict: true })
      .then(() => {
        fetcher.submit(
          { _action: 'step3', ...formData },
          { method: 'post' }
        );
      })
      .catch((err: yup.ValidationError) => {
        return toast.error(err.message, { theme: "colored", position: "bottom-center" });
      });
  };

  return (
    <div className="customer-data">

      <h2>Step 3 - Comunicazione</h2>

      <fetcher.Form method="post" onSubmit={onSubmit}>

        {/* <input type="hidden" name="_action" value="step2" /> */}

        <Allergies
          label={'Allergie'}
          values={form.allergies}
          className='data'
          setAllergies={(allergies) => setForm({ ...form, allergies })}
        />

        <TextAreaInput
          id='note'
          name='note'
          label='Note'
          className='data'
          yupSchema={step3Schema.fields.note as yup.StringSchema<string>}
          maxLength={255}
          rows={5}
          value={form.note ?? ''}
          onChange={(e) => setForm({ ...form, note: e.currentTarget.value })}
        />


        <section className="menu">
          <h3>Menù su prenotazione</h3>
          <p><span>I seguenti menù sono ordinabili al momento della prenotazione. Qualora foste interessati, spuntare la casella sottostante.</span></p>

          <RadioBoxInput
            id='menu'
            name='menu'
            selectedValue={form.menu ?? ''}
            onChange={(e) => setForm({ ...form, menu: e.currentTarget.value })}
            options={[
              {
                id: 'philippe',
                value: 'Chez Philippe',
                label: 'Menù "Chez Philippe"',
              },
              {
                id: 'cancale',
                value: 'Cancale',
                label: <span>Menù "Ritorno da Cancale" <span className="note">*</span></span>,
                disabled: () => {
                  let reservation = getReservation();
                  const dayOfWeek = new Date(reservation.date).getDay();
                  const month = new Date(reservation.date).getMonth();
                  const year = new Date(reservation.date).getFullYear();
                  if (year !== 2024) return true;
                  if (month !== 9 && month !== 10) return true;
                  if (dayOfWeek !== 3 && dayOfWeek !== 4) return true;
                  return false;
                }
              },
              {
                id: 'null',
                value: '',
                label: 'Nessuno',
              },
            ]}
          />
          <p><i><span className="note">*</span> Il Menù <strong>"Ritorno da Cancale"</strong>t viene proposto il Mercoledì ed il Giovedì (pranzo e cena) sino a fine Novembre.</i></p>
        </section>

        <section className="agreements">
          <h3>Consensi</h3>

          <p>Resta informato sulle nostre iniziative.</p>
          <CheckBoxInput
            id='mailingList'
            name='mailingList'
            label='Iscrizione alla mailing list'
            checked={form.mailingList ?? false}
            onChange={(e) => setForm({ ...form, mailingList: e.currentTarget.checked })}
          />

          <p>Aggiornamenti su nuovi menù e proposte.</p>
          <p><i>I dati saranno utilizzati internamente e non ceduti a terze parti.</i></p>
          <CheckBoxInput
            id='gdpr'
            name='gdpr'
            label='Accetto la privacy *'
            checked={form.gdpr}
            onChange={(e) => setForm({ ...form, gdpr: e.currentTarget.checked })}
            yupSchema={step3Schema.fields.gdpr as yup.BooleanSchema<boolean>}
            validateNow={validateAll}
            required={true}
          />
        </section>

        {<button type="submit" className="btn btn--dark" disabled={isSubmitting}>
          {isSubmitting ? (
            <span>Salvando...</span>
          ) : (
            <>
              <span>Conferma e Continua</span>
              {/* <CurrencyEuroIcon width={20} /> */}
            </>
          )}
        </button>}

      </fetcher.Form>

      {/* <pre>
        {JSON.stringify(form, null, 2)}
        {JSON.stringify(savedReservation, null, 2)}
      </pre> */}


    </div>
  )
}
export default Step3page;
